import { useState, useRef, useEffect, useMemo } from 'react';
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { getBelongingLayers } from '../../lib/getBelongingLayers';
import './MenuContainer.css';
import { useAtomValue } from 'jotai';
import { isSeparateDisplayControllerAtom, selectedMapIdAtom } from '../../atoms';
import { MenuCategoryProps } from '../../types/components/menuItem';
import { toggleSelectedLayers } from '../../lib/selectedLayersControl';
import MenuItem from './MenuItem';
import { MAIN_MAP_ID } from '../../constants/components/map';
import classNames from 'classnames';
import ReactGA from 'react-ga4';


const MenuCategory = ({ id, label, menuItems, selectedLayers, setSelectedLayers }: MenuCategoryProps) => {

    const [isOpen, setIsOpen] = useState(false);
    const checkboxRefCategory = useRef<HTMLInputElement>(null);
    const checkboxRefData = useRef<HTMLInputElement>(null);
    const selectedMapId = useAtomValue(selectedMapIdAtom);
    const isSeparateDisplay = useAtomValue(isSeparateDisplayControllerAtom);

    const selfAndChildLayers = useMemo(() => getBelongingLayers(menuItems), [menuItems]);

    useEffect(() => {
        if(!checkboxRefCategory.current || !selectedLayers) { return; }
        let isChecked = false;
        let isIndeterminate = false;
        if (!selectedLayers.length) {
            isChecked = false;
            isIndeterminate = false;
        } else {
            isChecked = selfAndChildLayers.every(layer => selectedLayers.includes(layer));
            isIndeterminate = !isChecked && selfAndChildLayers.some(layer => selectedLayers.includes(layer));
        }
        checkboxRefCategory.current.checked = isChecked;
        checkboxRefCategory.current.indeterminate = isIndeterminate;

        if(isChecked && selectedMapId === MAIN_MAP_ID) {
            //【GoogleAnalytics】メニューカテゴリ クリックトラッキング
            ReactGA.event({
                category: "menu_click", // カテゴリ（任意の名前）
                action: "click",          // イベントの種類
                label: `カテゴリー/${id}`,       // ボタン名
            });
        }
    }, [id, selectedLayers, selectedMapId, selfAndChildLayers]);


    const handleCategoryCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const newSelectedLayers = toggleSelectedLayers(selectedLayers, selfAndChildLayers, checked);
        setSelectedLayers(newSelectedLayers);
    };

    const toggleOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    return (
        <div
            className={classNames("sidebar-item-category", `${checkboxRefData.current?.checked && 'checked'}`, {
                'red-item': isSeparateDisplay && selectedMapId === MAIN_MAP_ID,
            })}
            data-category-id={id}
        >
            <div className="sidebar-item">
                <label className="label category-label" htmlFor={id}>
                    <input
                        id={id}
                        type="checkbox"
                        ref={checkboxRefCategory}
                        onChange={(e) => handleCategoryCheck(e)}
                    />
                    <span>{label}</span>
                </label>
                <button className="category-toggle" type="button" onClick={(e) => toggleOpen(e)}>
                    {isOpen ? <BsChevronDown /> : <BsChevronUp />}
                </button>
            </div>

            {isOpen && (
                <div className="sidebar-item-category-items">
                    {Object.entries(menuItems).map(([key, item]) => {
                        // 再帰条件を明確化
                        if (item.type === 'category' && item.items) {
                            return (
                                <MenuCategory
                                    key={item.id}
                                    id={item.id}
                                    label={key}
                                    menuItems={item.items} // 再帰で渡すときに正しいデータ構造を確認
                                    selectedLayers={selectedLayers}
                                    setSelectedLayers={setSelectedLayers}
                                />
                            );
                        }
                        if (item.type === 'data') {
                            return (
                                <MenuItem
                                    key={item.id}
                                    id={item.id}
                                    label={key}
                                    item={item}
                                    isAlert={'metadata' in item && item.metadata?.geometryType === 'Alert'}
                                    selectedLayers={selectedLayers}
                                    setSelectedLayers={setSelectedLayers}
                                />
                            );
                        }
                        return null;
                    })}
                </div>
            )}
        </div>
    );
};

export default MenuCategory;
